import type {
  EngagementHealth,
  FeatureActivity,
  FeatureAdoption,
  FeaturePlayers,
  RoomAttention,
  SessionClients,
  WeekVolume,
  WriterInstance,
} from '@brush/api-client';

/*
 * The admin Overview's rules (ADR 0012 Decision 16, step 13), kept out of the components so each is
 * a test rather than a reading of markup.
 *
 * THE ONE RULE ABOVE THE OTHERS: nothing on this screen draws a zero for something nobody is
 * counting. A feature no route records, a week before counting began, a card whose data arrives with
 * a later step — each rendered as 0 reads as a finding ("nobody uses fantasy") about a platform that
 * has simply not been asked yet. Each of those says so in words instead.
 */

export type SwitchKey = 'canLeaderboard' | 'canFantasy' | 'canRatings';

export interface FeatureEntry {
  feature: string;
  label: string;
  /** The room switch that turns the feature on, for the three features a room can switch. */
  switchKey: SwitchKey | null;
}

/**
 * Every catalogue feature, in the order the Overview reads them: the three a room switches on first,
 * since those are where investment is decided, then the rest of the product, then the platform's own.
 */
export const FEATURE_ORDER: readonly FeatureEntry[] = [
  { feature: 'leaderboards', label: 'Leaderboards', switchKey: 'canLeaderboard' },
  { feature: 'ratings', label: 'Ratings', switchKey: 'canRatings' },
  { feature: 'fantasy', label: 'Fantasy', switchKey: 'canFantasy' },
  { feature: 'tournaments', label: 'Tournaments', switchKey: null },
  { feature: 'points', label: 'Points systems', switchKey: null },
  { feature: 'templates', label: 'Templates', switchKey: null },
  { feature: 'rooms', label: 'Rooms and operators', switchKey: null },
  { feature: 'players', label: 'Players', switchKey: null },
  { feature: 'memberships', label: 'Connected accounts', switchKey: null },
  { feature: 'portal', label: 'Player portal', switchKey: null },
  { feature: 'console', label: 'Consoles', switchKey: null },
  { feature: 'platform', label: 'Platform configuration', switchKey: null },
];

/**
 * The server's features in the Overview's order. One this build does not know is shown under its own
 * name at the end rather than dropped: a feature added on the server before this screen learned of it
 * is still a feature somebody may be using.
 */
export function orderedFeatures(
  features: readonly FeatureActivity[],
): (FeatureEntry & { activity: FeatureActivity })[] {
  const rank = (feature: string) => {
    const i = FEATURE_ORDER.findIndex((f) => f.feature === feature);
    return i === -1 ? FEATURE_ORDER.length : i;
  };
  return [...features]
    .sort((a, b) => rank(a.feature) - rank(b.feature))
    .map((activity) => ({
      ...(FEATURE_ORDER.find((f) => f.feature === activity.feature) ??
        { feature: activity.feature, label: activity.feature, switchKey: null }),
      activity,
    }));
}

export type VolumeState =
  | { kind: 'not-collected' }
  | { kind: 'nothing-counted' }
  | { kind: 'weeks'; weeks: readonly WeekVolume[] };

/**
 * Which of three things a feature's volume card says. Not collected is decided by `collected` alone,
 * so a server that ever sent weeks for a feature nothing records still could not turn it into a row
 * of zeros; nothing counted yet is a collected feature on an environment the relay has not counted
 * anything on.
 */
export function volumeState(activity: FeatureActivity): VolumeState {
  if (activity.collected.length === 0) return { kind: 'not-collected' };
  if (activity.weeks.length === 0) return { kind: 'nothing-counted' };
  return { kind: 'weeks', weeks: activity.weeks };
}

export interface RoomSwitches {
  isInternal: boolean;
  canLeaderboard: boolean;
  canFantasy: boolean;
  canRatings: boolean;
}

/**
 * Rooms with the feature switched on, over the rooms that are not internal — a testing or demo room
 * is out of every product figure (Decision 10). A numerator and a denominator, never a ratio, for the
 * `Ratio` component.
 */
export function enabledRatio(rooms: readonly RoomSwitches[], key: SwitchKey) {
  const counted = rooms.filter((r) => !r.isInternal);
  return { numerator: counted.filter((r) => r[key]).length, denominator: counted.length };
}

/**
 * A `yyyy-mm-dd` day with a named month. Never `toLocaleDateString` on its own: 03/04 is two days
 * depending on who reads it. Formatted in UTC because the value is a date, not an instant.
 */
export function formatDay(day: string, withYear = true): string {
  const [y, m, d] = day.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    ...(withYear ? { year: 'numeric' } : {}),
    timeZone: 'UTC',
  });
}

export function weekLabel(week: WeekVolume): string {
  if (week.soFar) return 'So far this week';
  const label = `Week of ${formatDay(week.weekStart, false)}`;
  return week.partial ? `${label} (counting began mid-week)` : label;
}

/**
 * Null is said with the date counting began — "none since" is a statement about the record, which is
 * all it can be: an operator may well have worked before anything was counted.
 */
export function lastActivityText(day: string | null, collectedSince: string | null): string {
  if (day !== null) return formatDay(day);
  return collectedSince === null ? 'Nothing recorded yet' : `None since ${formatDay(collectedSince)}`;
}

export type EngagementCard =
  | { kind: 'not-collected'; says: string }
  | { kind: 'reported'; dropped: number; accepted: number };

/**
 * "Usage events dropped" (Decision 16). Null from the server means nothing records usage yet: the
 * writer reports idle windows from the start, so "none dropped of none" would claim a measurement
 * nobody made.
 */
export function engagementCard(engagement: EngagementHealth | null): EngagementCard {
  if (engagement === null) {
    return {
      kind: 'not-collected',
      says: 'Page views and similar usage the platform had to drop under load. Not collected yet — usage is not recorded yet.',
    };
  }
  return {
    kind: 'reported',
    dropped: engagement.dropped,
    accepted: engagement.written + engagement.dropped,
  };
}

/**
 * Platform health's line per writer instance. The server decides silent — three heartbeat intervals
 * with no report — and this only words it, in the viewer's own clock unless a zone is given (tests).
 */
export function writerLine(writer: WriterInstance, timeZone?: string): string {
  const time = new Date(writer.lastReportAt).toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    ...(timeZone ? { timeZone } : {}),
  });
  return writer.silent ? `Silent since ${time}` : `Last heard ${time}`;
}

export type SessionsCard =
  | { kind: 'not-collected'; says: string }
  | { kind: 'reported'; unrecognised: number; sessions: number };

/**
 * "Sessions from unrecognised apps" (Decision 16): a sign-in from an app the platform does not know
 * usually means a client id missing from configuration. Null from the server means nothing records
 * sessions, said in words rather than as "none of none".
 */
export function sessionsCard(sessions: SessionClients | null): SessionsCard {
  if (sessions === null) {
    return {
      kind: 'not-collected',
      says: 'Sign-ins from an app the platform does not know, which usually means a missing client id. Not collected yet — sessions are not recorded yet.',
    };
  }
  return { kind: 'reported', unrecognised: sessions.unrecognised, sessions: sessions.sessions };
}

export type AdoptionCard =
  | { kind: 'not-measured'; says: string }
  | { kind: 'measured'; month: string; numerator: number; denominator: number; idle: string[] };

/**
 * Adoption breadth and the idle rooms, for a switchable feature's last complete month (Decision 16, step
 * 18). The server reads both through one definition, so the ratio's counts and the names add up, and
 * leaves out a feature with no complete month — which is said, never drawn as zero: the first month is
 * reported only once every room has closed it.
 */
export function adoptionCard(feature: string, adoption: readonly FeatureAdoption[]): AdoptionCard {
  const entry = adoption.find((a) => a.feature === feature);
  if (!entry) {
    return {
      kind: 'not-measured',
      says: 'Not measured yet — the first month is reported once every room has closed it.',
    };
  }
  const [y, m] = entry.month.split('-').map(Number);
  return {
    kind: 'measured',
    month: new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' }),
    numerator: entry.roomsActive,
    denominator: entry.roomsEnabled,
    idle: entry.idleRooms.map((r) => r.name ?? 'A room since deleted'),
  };
}

export interface OperatorDaysLine {
  soFar: string;
  usual: string;
}

/**
 * A room's operator-days: this week so far, and its usual by this point of the week — the same days so
 * far of up to four prior weeks (Monday to Wednesday against four prior Mondays to Wednesdays), which is
 * why it reads "by now" and never "a week". Fewer than four weeks is said, since a usual from one is a
 * weaker claim; none is said too, and never drawn as zero, since a room nobody has measured yet is not a
 * room nobody works in. The table sorts on the difference; this only words the two figures.
 */
export function operatorDaysLine(room: RoomAttention): OperatorDaysLine {
  const soFar = `${room.operatorDaysThisWeek} so far this week`;
  if (room.operatorDaysUsual === null) return { soFar, usual: 'No usual week yet' };
  const usual = `Usually ${Number(room.operatorDaysUsual.toFixed(1))} by now`;
  return {
    soFar,
    usual: room.usualWeeks < 4 ? `${usual}, from ${room.usualWeeks} week${room.usualWeeks === 1 ? '' : 's'}` : usual,
  };
}

export interface ActivePlayersLine {
  soFar: string;
  lastMonth: string;
}

/**
 * A room's active players (Decision 16, step 19): this month so far in the room's own calendar, read live,
 * and last month as the period job stored it. Last month exists only once the room has closed it, so
 * before then it is said — never 0, since a room whose month nobody has measured is not a room nobody
 * played at. A month so far against a whole month is the comparison the card asks for; the words say which
 * is which.
 */
export function activePlayersLine(room: RoomAttention): ActivePlayersLine {
  return {
    soFar: `${room.activePlayersThisMonth} so far this month`,
    lastMonth: room.activePlayersLastMonth === null ? 'Last month not measured yet' : `${room.activePlayersLastMonth} last month`,
  };
}

export interface FeaturePlayersCard {
  title: string;
  soFar: string;
  lastMonth: string;
}

/** A month's name from its first day, `yyyy-mm-01`, read in UTC so no zone moves it into the month before. */
function monthName(firstDay: string, monthsBack = 0): string {
  const [y, m] = firstDay.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1 - monthsBack, 1)).toLocaleDateString('en-US', { month: 'long', timeZone: 'UTC' });
}

/**
 * Distinct players per feature, platform-wide (Decision 16, step 19), labelled "players who used X": an
 * account counts once however many rooms it used the feature at. The server lists only the features a player reaches in the portal, and only
 * while something records them, so a feature it does not list has no such figure — null, and no line, rather
 * than a "not collected" that would imply rooms or templates someday will have players; a feature nothing
 * records already says so in its volume block. Last month is the stored platform row, which exists only once
 * every room has closed that month; until then that is said, never drawn as 0.
 */
export function featurePlayersCard(feature: string, label: string, players: readonly FeaturePlayers[]): FeaturePlayersCard | null {
  const entry = players.find((p) => p.feature === feature);
  if (!entry) return null;
  const last = monthName(entry.month, 1);
  return {
    title: `Players who used ${label}`,
    soFar: `${entry.soFar} so far in ${monthName(entry.month)}`,
    lastMonth: entry.lastMonth === null ? `${last} not measured yet` : `${entry.lastMonth} in ${last}`,
  };
}

/**
 * What "Rooms needing attention" sorts by. Generic on purpose: each room's week is its own gaming
 * calendar, so on any given day the rooms can be at different days of their week — a sentence naming
 * days ("Monday to Wednesday") is wrong for most of the week and for every room in another zone.
 */
export const ROOMS_NEEDING_ATTENTION_INTRO =
  'Active rooms that are not internal, furthest behind first: each room’s operator-days so far this week against the same days of its four weeks before, every room read in its own gaming calendar — the same days rather than a weekly average, since poker weeks are weekend-heavy. Rooms with no weeks measured yet follow, those whose staff have not worked for longest first.';

